import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Ban,
  Ellipsis,
  KeyRound,
  Plus,
  ShieldCheck,
  ShieldMinus,
  ShieldPlus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, FormError, Input } from "@/components/ui/field";
import { DropdownMenu, MenuItem, MenuSeparator } from "@/components/ui/menu";
import { ConfigRow } from "@/features/admin/config-row";
import { DomainsPanel } from "@/features/admin/domains";
import { AuditList, PageHeader, Rows } from "@/features/admin/parts";
import { FormatPicker } from "@/features/configs/format-picker";
import { TrafficChart, TrafficTiles } from "@/features/traffic/traffic";
import type { Schemas } from "@/lib/api/client";
import {
  type ConfigFormat,
  type MetricsRange,
  type User,
  useBlockUser,
  useDeleteUser,
  useIssueConfig,
  useResetPassword,
  userQuery,
  userTrafficQuery,
  useSetLimit,
  useSetNote,
  useSetRole,
  useSignOutUser,
  useUnblockUser,
} from "@/lib/api/queries";
import { describeUserAgent, formatDate, formatRelative } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/users/$userId")({
  params: {
    parse: ({ userId }) => ({ userId: Number(userId) }),
    stringify: ({ userId }) => ({ userId: String(userId) }),
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(userQuery(params.userId)),
  component: UserPage,
});

function UserPage() {
  const { userId } = Route.useParams();
  const { data } = useSuspenseQuery(userQuery(userId));
  const { session } = Route.useRouteContext();
  const { user, note, invite, sessions, configs, events, connections, traffic } = data;
  const selfUsed = configs.filter((c) => c.counts_towards_limit).length;

  return (
    <>
      <PageHeader
        title={user.username}
        actions={<UserActions user={user} isSelf={session.user.id === user.id} />}
      />
      <p className="mt-2 text-sm text-muted">
        {user.role === "admin" ? "Администратор" : "Пользователь"}, с {formatDate(user.created_at)}
        {user.status === "blocked" && <span className="text-danger">, заблокирован</span>}
        {invite && (
          <>
            . Пришёл по инвайту <span className="font-display text-text">{invite.code}</span>
            {invite.note && ` (${invite.note})`}
          </>
        )}
      </p>
      <NoteEditor key={note ?? ""} userId={user.id} note={note} />

      <TrafficTiles totals={traffic} configs={configs} className="mt-6" />
      {configs.length > 0 && <UserTraffic userId={user.id} />}
      {configs.length > 0 && <DomainsPanel userId={user.id} configs={configs} />}

      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base">Конфиги</h2>
          <LimitEditor user={user} used={selfUsed} />
        </div>
        <div className="mt-3">
          {configs.length === 0 ? (
            <p className="text-sm text-muted">Конфигов нет.</p>
          ) : (
            <Rows>
              {configs.map((config) => (
                <ConfigRow key={config.id} config={config} />
              ))}
            </Rows>
          )}
        </div>
      </section>

      <UserSessions userId={user.id} sessions={sessions} />

      <section className="mt-10">
        <h2 className="text-base">Подключения</h2>
        <p className="mt-1 text-sm text-muted">
          Смена IP посреди подключения — это переезд между сетями или один конфиг на двух
          устройствах.
        </p>
        <div className="mt-3">
          <AuditList entries={connections} />
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-base">Последние события</h2>
        <div className="mt-3">
          <AuditList entries={events} />
        </div>
      </section>
    </>
  );
}

function UserTraffic({ userId }: { userId: number }) {
  const [range, setRange] = useState<MetricsRange>("7d");
  const { data } = useQuery({
    ...userTrafficQuery(userId, range),
    placeholderData: keepPreviousData,
  });
  if (!data) return null;
  return <TrafficChart data={data} range={range} onRangeChange={setRange} className="mt-4" />;
}

function UserActions({ user, isSelf }: { user: User; isSelf: boolean }) {
  const [dialog, setDialog] = useState<"block" | "issue" | "reset" | "delete" | null>(null);
  const block = useBlockUser();
  const setRole = useSetRole();
  const makeAdmin = user.role !== "admin";
  const unblock = useUnblockUser();
  const blocked = user.status === "blocked";
  const close = (open: boolean) => !open && setDialog(null);

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setDialog("issue")} disabled={blocked}>
        <Plus aria-hidden />
        Выдать конфиг
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setDialog("reset")}>
        <KeyRound aria-hidden />
        Сбросить пароль
      </Button>
      {!isSelf &&
        (blocked ? (
          <Button
            size="sm"
            variant="secondary"
            pending={unblock.isPending}
            onClick={() =>
              unblock.mutate(user.id, {
                onSuccess: () => toast.success("Разблокирован, конфиги снова работают"),
                onError: (error) => toast.error(error.message),
              })
            }
          >
            <ShieldCheck aria-hidden />
            Разблокировать
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="text-danger"
            onClick={() => setDialog("block")}
          >
            <Ban aria-hidden />
            Заблокировать
          </Button>
        ))}

      <ConfirmDialog
        open={dialog === "block"}
        onOpenChange={close}
        title={`Заблокировать ${user.username}?`}
        description="Пользователя разлогинит, все его конфиги перестанут работать. После разблокировки они заработают снова с теми же ключами."
        confirmLabel="Заблокировать"
        danger
        pending={block.isPending}
        onConfirm={() =>
          block.mutate(user.id, {
            onSuccess: () => {
              toast.success("Заблокирован");
              setDialog(null);
            },
            onError: (error) => toast.error(error.message),
          })
        }
      >
        {null}
      </ConfirmDialog>
      {!isSelf && (
        <DropdownMenu
          trigger={
            <Button size="sm" variant="ghost" aria-label="Ещё действия">
              <Ellipsis aria-hidden />
            </Button>
          }
        >
          <MenuItem
            onSelect={() =>
              setRole.mutate(
                { id: user.id, role: makeAdmin ? "admin" : "user" },
                {
                  onSuccess: () =>
                    toast.success(
                      makeAdmin ? "Теперь администратор" : "Права администратора сняты",
                    ),
                  onError: (error) => toast.error(error.message),
                },
              )
            }
          >
            {makeAdmin ? <ShieldPlus aria-hidden /> : <ShieldMinus aria-hidden />}
            {makeAdmin ? "Сделать администратором" : "Снять права администратора"}
          </MenuItem>
          <MenuSeparator />
          <MenuItem onSelect={() => setDialog("delete")}>
            <Trash2 aria-hidden />
            <span className="text-danger">Удалить пользователя</span>
          </MenuItem>
        </DropdownMenu>
      )}
      <IssueDialog user={user} open={dialog === "issue"} onOpenChange={close} />
      <DeleteUserDialog user={user} open={dialog === "delete"} onOpenChange={close} />
      <ResetPasswordDialog user={user} open={dialog === "reset"} onOpenChange={close} />
    </>
  );
}

function IssueDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const issue = useIssueConfig();
  const [format, setFormat] = useState<ConfigFormat>("amneziawg");
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Выдать конфиг"
      description={`Дополнительный конфиг для ${user.username}. В его личный лимит не входит.`}
    >
      <div className="grid gap-4">
        <FormatPicker value={format} onChange={setFormat} name="issue-format" />
        <FormError message={issue.error?.message} />
        <Button
          pending={issue.isPending}
          onClick={() =>
            issue.mutate(
              { userId: user.id, format },
              {
                onSuccess: () => {
                  toast.success("Конфиг выдан");
                  onOpenChange(false);
                },
              },
            )
          }
        >
          Выдать
        </Button>
      </div>
    </Dialog>
  );
}

function ResetPasswordDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const reset = useResetPassword();
  const password = reset.data?.password;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset.reset();
        onOpenChange(next);
      }}
      title="Сброс пароля"
      description={
        password
          ? "Передайте пароль пользователю. Больше он нигде не покажется."
          : `Для ${user.username} будет создан новый пароль, все его сессии завершатся.`
      }
    >
      {password ? (
        <div className="grid gap-3">
          <output className="block rounded-xl bg-surface-2 px-4 py-3 text-center font-display text-lg tracking-wide select-all">
            {password}
          </output>
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(password);
              toast.success("Пароль скопирован");
            }}
          >
            Скопировать
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          <FormError message={reset.error?.message} />
          <Button variant="danger" pending={reset.isPending} onClick={() => reset.mutate(user.id)}>
            Сбросить пароль
          </Button>
        </div>
      )}
    </Dialog>
  );
}

function LimitEditor({ user, used }: { user: User; used: number }) {
  const setLimit = useSetLimit();
  const [value, setValue] = useState(String(user.self_config_limit));
  const changed = Number(value) !== user.self_config_limit;

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        setLimit.mutate(
          { id: user.id, limit: Number(value) },
          {
            onSuccess: () => toast.success("Лимит сохранён"),
            onError: (error) => toast.error(error.message),
          },
        );
      }}
    >
      <Field label={`Своих: ${used} из`}>
        {(control) => (
          <Input
            {...control}
            type="number"
            min={0}
            max={20}
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-9 w-20 rounded-lg"
          />
        )}
      </Field>
      {changed && (
        <Button size="sm" type="submit" pending={setLimit.isPending}>
          Сохранить
        </Button>
      )}
    </form>
  );
}

function NoteEditor({ userId, note }: { userId: number; note: string | null }) {
  const save = useSetNote();
  const [value, setValue] = useState(note ?? "");
  const changed = value.trim() !== (note ?? "");

  return (
    <form
      className="mt-4 flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(
          { id: userId, note: value.trim() || null },
          {
            onSuccess: () => toast.success("Заметка сохранена"),
            onError: (error) => toast.error(error.message),
          },
        );
      }}
    >
      <div className="flex-1">
        <Field label="Заметка" hint="Видна только администраторам">
          {(control) => (
            <Input
              {...control}
              value={value}
              maxLength={500}
              placeholder="Кто это, откуда знаем"
              onChange={(e) => setValue(e.target.value)}
              className="h-10 rounded-lg"
            />
          )}
        </Field>
      </div>
      {changed && (
        <Button size="sm" type="submit" pending={save.isPending} className="mb-6">
          Сохранить
        </Button>
      )}
    </form>
  );
}

function UserSessions({
  userId,
  sessions,
}: {
  userId: number;
  sessions: Schemas["ActiveSessionOut"][];
}) {
  const signOut = useSignOutUser();
  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base">Где выполнен вход</h2>
        {sessions.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            pending={signOut.isPending}
            onClick={() =>
              signOut.mutate(userId, {
                onSuccess: () => toast.success("Разлогинен на всех устройствах"),
                onError: (error) => toast.error(error.message),
              })
            }
          >
            Разлогинить везде
          </Button>
        )}
      </div>
      <p className="mt-1 text-sm text-muted">VPN-конфиги при этом продолжают работать.</p>
      <div className="mt-3">
        {sessions.length === 0 ? (
          <p className="text-sm text-muted">Нигде не вошёл.</p>
        ) : (
          <Rows>
            {sessions.map((s) => (
              <li key={s.id} className="px-4 py-3">
                <p>{describeUserAgent(s.user_agent)}</p>
                <p className="text-sm text-muted">
                  {[s.ip, formatRelative(s.last_used_at), `вход ${formatDate(s.created_at)}`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </li>
            ))}
          </Rows>
        )}
      </div>
    </section>
  );
}

function DeleteUserDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const remove = useDeleteUser();
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped("");
        onOpenChange(next);
      }}
      title={`Удалить ${user.username}?`}
      description="Все его конфиги перестанут работать и пропадут с сервера. Аккаунт удалится насовсем, история трафика и журнал останутся."
      confirmLabel="Удалить навсегда"
      danger
      confirmDisabled={typed !== user.username}
      pending={remove.isPending}
      onConfirm={() =>
        remove.mutate(
          { id: user.id, confirmUsername: typed },
          {
            onSuccess: async () => {
              toast.success(`${user.username} удалён`);
              await navigate({ to: "/admin/users" });
              await remove.refresh();
            },
            onError: (error) => toast.error(error.message),
          },
        )
      }
    >
      <Field label={`Введите логин «${user.username}» для подтверждения`}>
        {(control) => (
          <Input
            {...control}
            value={typed}
            autoComplete="off"
            onChange={(e) => setTyped(e.target.value)}
          />
        )}
      </Field>
    </ConfirmDialog>
  );
}
